// 이 쪽의 글: every text an output page prints, top to bottom, each one edited right there — where
// it is kept decides nothing here: an editor that owns its address (the product's field in the
// Studio document, a manuscript, a frame word, the catalogue) loads and saves it. A value the output
// computes is shown as it prints. The page's boxes (output-parts.mjs) lead to their fields, and the
// colors of the boxes are explained above the list, by the kinds of part the output names.
import { drawn, kindOf } from './output-parts.mjs';

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}
const names = text => [...String(text).matchAll(/\{([a-z_][a-z0-9_]*)\}/g)].map(match => match[1]).sort().join(' ');

/**
 * editor: { owns(address), label(address), noteOf(addresses), load(address, pending), save(edit), revert(address) }
 * (output-panel.mjs joins the Studio document's and the output's). onEdited(address, pending) hears a save or an undo,
 * onHover(partIndex, on) the pointer over a text's row (its box on the page lights up).
 */
export function mountPageTexts({ onEdited = () => {}, onHover = () => {} } = {}) {
  const heading = node('h3', { text: '이 쪽의 글' });
  const note = node('p', { class: 'es-page-texts-note' });
  // What the colors of the boxes on the page mean, for the kinds this page has: the kinds on the
  // summary line, what each one is when opened.
  const kindLine = node('span', { class: 'es-page-texts-kinds' });
  const meanings = node('ul', { class: 'es-page-texts-meanings' });
  const legend = node('details', { class: 'es-page-texts-legend', hidden: '', 'data-testid': 'studio-page-texts-legend' }, [
    node('summary', { title: '쪽 위 상자의 색이 뜻하는 것' }, [kindLine, node('span', { class: 'es-page-texts-legend-more', text: '색 설명' })]), meanings]);
  const list = node('ol', { class: 'es-page-texts-list', 'data-testid': 'studio-page-texts-list' });
  const element = node('aside', { class: 'es-page-texts', hidden: '', 'aria-label': '이 쪽의 글', 'data-testid': 'studio-page-texts' }, [heading, legend, note, list]);
  let ticket = 0, shownParts = [], kinds = {};

  function swatch(kindName) {
    const kind = kindOf(kinds, kindName);
    const mark = node('span', { class: `es-page-texts-swatch${kind.outline ? ' es-page-texts-swatch--outline' : ''}`, 'aria-hidden': 'true' });
    mark.style.setProperty('--part-color', kind.color);
    return mark;
  }
  function showLegend(parts) {
    // In the order the output names its kinds; a kind it does not name comes last.
    const rank = kind => { const at = Object.keys(kinds).indexOf(kind); return at < 0 ? Infinity : at; };
    const present = [...new Set(parts.filter(drawn).map(part => part.kind))].sort((a, b) => rank(a) - rank(b))
      .map(kind => [kind, parts.filter(part => drawn(part) && part.kind === kind).length]);
    legend.hidden = !present.length;
    kindLine.replaceChildren(...present.map(([kind, count]) => node('span', { class: 'es-page-texts-kind', 'data-kind': kind, title: kindOf(kinds, kind).note }, [
      swatch(kind), node('span', { text: `${kindOf(kinds, kind).label} ${count}` })])));
    meanings.replaceChildren(...present.map(([kind]) => node('li', { 'data-kind': kind }, [
      swatch(kind), node('strong', { text: kindOf(kinds, kind).label }), node('span', { text: kindOf(kinds, kind).note })])));
  }
  /** Lights up the rows of a part's texts, while its box on the page is pointed at. */
  function light(partIndex, on) {
    for (const row of list.querySelectorAll(`[data-part="${partIndex}"]`)) row.classList.toggle('es-page-texts-item--lit', on);
  }
  function linked(row, part) {
    row.addEventListener('mouseenter', () => onHover(part.index, true));
    row.addEventListener('mouseleave', () => onHover(part.index, false));
    return row;
  }
  const fixedRow = (part, label, value = null) => linked(node('li', { class: 'es-page-texts-item es-page-texts-item--fixed', 'data-part': String(part.index), 'data-kind': part.kind,
    'data-testid': 'studio-page-texts-fixed' }, [node('span', { class: 'es-page-texts-head' }, [swatch(part.kind), node('span', { class: 'es-page-texts-label', text: label })]),
    ...(value === null ? [] : [node('span', { class: 'es-page-texts-value', text: value })])]), part);

  function field(entry, address, editor, part) {
    const label = node('span', { class: 'es-page-texts-label', text: editor.label(address) ?? part.label });
    const badge = node('span', { class: 'es-page-texts-badge', text: '고침', hidden: '' });
    // As tall as the text (its lines, about 30 characters to a line), up to eight lines.
    const lines = entry.text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 30)), 0);
    const area = node('textarea', { rows: String(Math.min(8, lines)), 'data-address': address, 'data-testid': 'studio-page-text' });
    area.value = entry.text;
    const review = entry.picture ? node('label', { class: 'es-page-texts-review' }, [
      node('input', { type: 'checkbox', 'data-testid': 'studio-page-text-reviewed' }), node('span', { text: `짝지은 그림 “${entry.picture}”이 새 글과 맞는지 확인했습니다` })]) : null;
    const fills = entry.fills?.length ? node('small', { class: 'es-page-texts-fills', text: `${entry.fills.map(name => `{${name}}`).join(' ')}는 책이 쪽마다 채우는 값입니다.` }) : null;
    const save = node('button', { type: 'button', class: 'es-page-texts-save', 'data-testid': 'studio-page-text-save', text: '저장', disabled: '' });
    const undo = node('button', { type: 'button', class: 'es-page-texts-undo', 'data-testid': 'studio-page-text-revert', text: '원래 글로', hidden: '' });
    const status = node('small', { class: 'es-page-texts-status', 'aria-live': 'polite' });
    const row = linked(node('li', { class: 'es-page-texts-item', 'data-address': address, 'data-part': String(part.index), 'data-kind': part.kind, 'data-testid': 'studio-page-texts-item' }, [
      node('div', { class: 'es-page-texts-head' }, [swatch(part.kind), label, badge]), area, ...(fills ? [fills] : []), ...(review ? [review] : []),
      node('div', { class: 'es-page-texts-actions' }, [save, undo, status])]), part);
    let saved = entry.text;
    const mark = pending => { badge.hidden = !pending; undo.hidden = !pending; row.classList.toggle('es-page-texts-item--edited', pending); };
    mark(entry.pending);
    area.addEventListener('input', () => { save.disabled = area.value === saved; status.textContent = ''; });
    area.addEventListener('keydown', event => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); save.click(); } });
    save.addEventListener('click', async () => {
      if (names(area.value) !== names(saved)) { status.textContent = '{ }로 둘러싼 칸은 책이 채우는 값이라 빼거나 새로 넣을 수 없습니다.'; return; }
      save.disabled = true; status.textContent = '저장하는 중…';
      try {
        const result = await editor.save({ address, base: saved, value: area.value, reviewed: Boolean(review?.querySelector('input').checked) });
        saved = area.value;
        mark(result?.pending ?? true);
        status.textContent = '저장했습니다';
        onEdited(address, result?.pending ?? true);
      } catch (error) { status.textContent = error.message; save.disabled = false; }
    });
    undo.addEventListener('click', async () => {
      try {
        const result = await editor.revert(address);
        area.value = saved = result.text;
        mark(false); save.disabled = true; status.textContent = '원래 글로 되돌렸습니다';
        onEdited(address, false);
      } catch (error) { status.textContent = error.message; }
    });
    return row;
  }

  /** The list before its page's parts are in: the page's number, and that they are coming. */
  function loading(page) {
    ticket += 1; shownParts = [];
    element.hidden = false; legend.hidden = true;
    heading.textContent = `${page.number}쪽의 글`;
    note.textContent = '불러오는 중…';
    list.replaceChildren();
  }
  /** Says why `page` lists no texts: it has no parts (a page drawn without them), or they did not load. */
  function empty(page, reason = '이 쪽은 글 조각 정보가 없어 여기서 글을 고칠 수 없습니다.') {
    loading(page);
    note.textContent = reason;
  }
  /**
   * Lists the texts of `page` from its parts (`found`, output-parts.mjs partsOf): editable where `editor`
   * owns their address (null: read only), `pending` its edits waiting, `kinds` the output's kinds of part.
   */
  async function show(page, found, { editor = null, pending = [], kinds: partKinds = {} } = {}) {
    loading(page);
    const mine = ticket;
    kinds = partKinds; shownParts = found.parts;
    showLegend(shownParts);
    const texts = shownParts.map((part, index) => ({ ...part, index })).filter(part => 'text' in part)
      .sort((a, b) => a.y - b.y || a.x - b.x);
    const seen = new Set(), rows = [];
    for (const part of texts) {
      const owned = editor ? (part.edits ?? []).filter(address => editor.owns(address) && !seen.has(address)) : [];
      owned.forEach(address => seen.add(address));
      if (owned.length) {
        for (const address of owned) {
          let entry = null;
          try { entry = await editor.load(address, pending); } catch (error) { rows.push(fixedRow(part, `${editor.label(address) ?? part.label} · ${error.message}`)); }
          if (mine !== ticket) return;
          if (entry) rows.push(field(entry, address, editor, part));
        }
      } else if (!(part.edits ?? []).some(address => seen.has(address))) {
        // A value the output computes, or text kept where it is not edited here (the product's name).
        rows.push(fixedRow(part, part.label, part.text));
      }
    }
    list.replaceChildren(...rows);
    // Text with an address no editor here answers for (shared text, while the local workshop is off).
    const elsewhere = texts.some(part => (part.edits ?? []).length && !part.edits.some(address => editor?.owns(address)));
    note.textContent = [!editor && !elsewhere ? '이 화면에서는 글을 볼 수만 있습니다.' : '',
      editor && seen.size ? editor.noteOf([...seen]) : '',
      elsewhere ? '여러 책이 함께 쓰는 글(원고·틀 문구)은 결과물을 만드는 로컬 제작실이 켜져 있을 때 여기서 고칩니다.' : '',
      !seen.size && !elsewhere ? '이 쪽의 글은 모두 계산해 채우는 값이라 여기서 고치지 않습니다.' : ''].filter(Boolean).join(' ');
  }
  /** Brings the field of a part (its index on the page) into view and focuses it; a text printed twice has its field once. */
  function focus(partIndex) {
    const row = list.querySelector(`[data-part="${partIndex}"]`)
      ?? (shownParts[partIndex]?.edits ?? []).map(address => list.querySelector(`[data-address="${CSS.escape(address)}"]`)).find(Boolean);
    row?.scrollIntoView({ block: 'nearest' });
    row?.querySelector('textarea')?.focus();
    return Boolean(row);
  }
  function clear() { ticket += 1; shownParts = []; element.hidden = true; list.replaceChildren(); }
  return { element, loading, show, empty, focus, light, clear };
}
